/* eslint-disable no-constant-condition */
/**
 * @file EducationalMaterialCategoriesView.jsx
 * @author Fernando Ruano
 * @description Education Material Categories View Page
 */

// Dependencies
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Col, Collapse, Row, Button, Popconfirm } from 'antd';
import styled from 'styled-components';
import {
  PlusCircleFilled,
  EditOutlined,
  DeleteOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';

// Helper
import { getStorageItem } from 'commons/helpers/storage-helper';

// Components
import SectionFolder from '../components/sectionFolder';
import NewSectionFolder from '../components/newSectionFolder';
import ModalEM from '../components/ModalInputEM';

const { Panel } = Collapse;

const StyledPanel = styled(Panel)`
  background-color: white;
  border: solid 1px #bac3d4;
  font-weight: 500;
  color: var(--color-baxter-gray);
  font-size: 16px;

  .ant-collapse-content {
    border-top: 1px solid #d9d9d9 !important;
  }
`;

const CreateButton = styled(Button)`
  margin-left: 8px;
  border-color: var(--color-baxter-teal-medium) !important;
  background-color: var(--color-baxter-teal-medium) !important;
  color: white;
  box-sizing: border-box;
  border-radius: 5px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: auto;
`;

/**
 * EducationalMaterial Management View
 * @param { Object } educationalMaterialActions - EducationalMaterial Actions.
 * @return { Node } React node.
 */

function EducationalMaterialCategoriesView({
  educationalCategoryActions,
  userActions
}) {
  const { t: translate } = useTranslation();
  const [isPatient, setIsPatient] = useState(false);
  const [categories, setCategories] = useState([]);
  const [sections, setSections] = useState([]);
  const [visible, setVisible] = useState(false);
  const [visibleEdit, setVisibleEdit] = useState(false);
  const [createType, setCreateType] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [itemSelected, setItemSelected] = useState(null);
  const [permissions, setPermissions] = useState({
    read: false,
    save: false,
    update: false,
    delete: false
  });

  const {
    getEducationalCategoriesArray,
    createEducationalCategory,
    updateEducationalCategory,
    deleteEducationalCategoryById
  } = educationalCategoryActions;

  const { getValidatePermission } = userActions;

  const setPermissionsData = async (_, resp) => {
    setPermissions(resp);
  };

  const handleGetAllPermissions = async () => {
    const user = await getStorageItem('user');
    const theUserIsPatient = await getStorageItem('isPatient');
    const getData = () => {
      return {
        params: {
          moduleName: 'Aprendo a cuidarme',
          userName: theUserIsPatient ? user.username : user.user.username
        }
      };
    };
    getValidatePermission(getData(), setPermissionsData);
  };

  const hasSections = (item, sectionsArray) => {
    const childrenSection = sectionsArray.filter(
      ({ parent }) =>
        parent?.id_education_category === item.id_education_category
    );
    return childrenSection.length > 0;
  };

  const setData = async (_, resp) => {
    const isPtnt = await getStorageItem('isPatient');
    const respCategories = resp.data.filter(({ parent }) => !parent);
    const respSections = resp.data.filter(({ parent }) => !!parent);
    setCategories(
      !isPtnt
        ? respCategories
        : respCategories.filter((itm) => hasSections(itm, respSections))
    );
    setSections(respSections);
  };

  const onGetEducationalItems = () => {
    const data = { paged: false, limit: 10, page: 0 };
    getEducationalCategoriesArray(data, setData);
  };

  useEffect(async () => {
    setIsPatient(await getStorageItem('isPatient'));
    onGetEducationalItems();
  }, []);

  useEffect(() => {
    if (!isPatient) {
      handleGetAllPermissions();
    }
  }, [isPatient]);

  const afterCreateUpdate = () => {
    onGetEducationalItems();
    setVisible(false);
    setVisibleEdit(false);
  };

  const onCreateCategorySection = () => {
    const data = {
      description: inputValue,
      name: inputValue,
      parent: itemSelected
    };
    createEducationalCategory(data, afterCreateUpdate);
  };

  const onUpdateCategorySection = () => {
    const data = {
      ...itemSelected,
      id: itemSelected.id_education_category,
      description: inputValue,
      name: inputValue
    };
    updateEducationalCategory(data, afterCreateUpdate);
  };

  const onOpenCreate = (item, type) => {
    setItemSelected(item);
    setCreateType(type);
    setInputValue('');
    setVisible(true);
  };

  const onOpenCreateCategory = () => {
    onOpenCreate(null, 'category');
  };

  const onOpenCreateSection = (item) => {
    onOpenCreate(item, 'section');
  };

  const onOpenEdit = (item, type) => {
    setItemSelected(item);
    setInputValue(item.name);
    setCreateType(type);
    setVisibleEdit(true);
  };

  const onOpenEditCategory = (e, item) => {
    e.stopPropagation();
    onOpenEdit(item, 'category');
  };

  const onOpenEditSection = (item) => {
    onOpenEdit(item, 'section');
  };

  const onOpenDelete = (item) => {
    const data = {
      id: item.id_education_category
    };
    deleteEducationalCategoryById(data, onGetEducationalItems);
  };

  const getSectionsByParentId = (category) => {
    const childrenSection = sections.filter(
      ({ parent }) =>
        parent?.id_education_category === category.id_education_category
    );

    return (
      <Row gutter={20}>
        {childrenSection.map((section, index) => (
          <Col xs={24} md={12} key={index}>
            <SectionFolder
              categoryData={section}
              onDeleteFunc={() => onOpenDelete(section)}
              onEditFunc={() => onOpenEditSection(section)}
              name={section.name}
              permissions={permissions}
              link={`/main/educational-material/category/${section.name}/${section.id_education_category}`}
            />
          </Col>
        ))}
        {!isPatient && permissions.save && (
          <Col xs={24} md={12}>
            <NewSectionFolder
              onClickAction={() => onOpenCreateSection(category)}
            />
          </Col>
        )}
      </Row>
    );
  };

  const validateInput = inputValue.replace(/\s/g, '').length === 0;

  return (
    <div className="p-4 h-full text-baxterGray">
      {(permissions.read || isPatient) && (
        <>
          <div className="w-full flex flex-row justify-between items-center mb-8">
            <h1 className="text-base text-baxterGray font-bold mb-0">
              {translate('dashboard.Categories')}
            </h1>
            {!isPatient && permissions.save && (
              <CreateButton
                onClick={onOpenCreateCategory}
                icon={<PlusCircleFilled style={{ color: 'white' }} />}>
                {translate('educationalMaterial.createCat')}
              </CreateButton>
            )}
          </div>

          <section>
            <Collapse accordion style={{ border: 'solid 0px #bac3d4' }}>
              {categories.map((category, index) => (
                <StyledPanel
                  extra={
                    !isPatient && (
                      <div>
                        {!hasSections(category, sections) ? (
                          <Popconfirm
                            title={translate('commons.deleteCategory')}
                            cancelText={translate('commons.cancel')}
                            okText={translate('commons.confirm')}
                            onConfirm={() => onOpenDelete(category)}
                            icon={
                              <QuestionCircleOutlined
                                style={{ color: '#FA7268' }}
                              />
                            }>
                            {permissions.delete && (
                              <DeleteOutlined
                                onClick={(e) => e.stopPropagation()}
                                className="float-right"
                                style={{
                                  fontSize: 16,
                                  color: 'var(--color-error)'
                                }}
                              />
                            )}
                          </Popconfirm>
                        ) : (
                          <Popconfirm
                            title={translate('commons.cantDeleteCategory')}
                            cancelText={() => null}
                            okText={translate('Ok')}
                            icon={
                              <QuestionCircleOutlined
                                style={{ color: '#FA7268' }}
                              />
                            }>
                            {permissions.delete && (
                              <DeleteOutlined
                                onClick={(e) => e.stopPropagation()}
                                className="float-right"
                                style={{
                                  fontSize: 16,
                                  color: 'var(--color-error)'
                                }}
                              />
                            )}
                          </Popconfirm>
                        )}
                        {permissions.update && (
                          <EditOutlined
                            onClick={(e) => onOpenEditCategory(e, category)}
                            className="float-right mr-3"
                            style={{
                              fontSize: 16,
                              color: 'var(--color-place-holder)'
                            }}
                          />
                        )}
                      </div>
                    )
                  }
                  style={{
                    borderTopRightRadius: index === 0 ? 10 : 0,
                    borderTopLeftRadius: index === 0 ? 10 : 0,
                    borderBottom:
                      categories.length - 1 === index ? 'solid 1px #bac3d4' : 0,
                    borderBottomRightRadius:
                      categories.length - 1 === index ? 10 : 0,
                    borderBottomLeftRadius:
                      categories.length - 1 === index ? 10 : 0
                  }}
                  header={category.name}
                  key={index}>
                  {getSectionsByParentId(category)}
                </StyledPanel>
              ))}
            </Collapse>
            <ModalEM
              visible={visible}
              setVisible={setVisible}
              inputValue={inputValue}
              setInputValue={setInputValue}
              onActionConfirm={onCreateCategorySection}
              validateInput={validateInput}
              type={createType}
              title={translate(
                createType === 'category'
                  ? 'educationalMaterial.createCat'
                  : 'educationalMaterial.createSec'
              )}
              inputLabel={translate(
                createType === 'category'
                  ? 'educationalMaterial.categoryName'
                  : 'educationalMaterial.sectionName'
              )}
            />
            <ModalEM
              visible={visibleEdit}
              setVisible={setVisibleEdit}
              inputValue={inputValue}
              setInputValue={setInputValue}
              onActionConfirm={onUpdateCategorySection}
              validateInput={validateInput}
              type={createType}
              title={translate(
                createType === 'category'
                  ? 'educationalMaterial.editCategory'
                  : 'educationalMaterial.editSection'
              )}
              inputLabel={translate(
                createType === 'category'
                  ? 'educationalMaterial.categoryName'
                  : 'educationalMaterial.sectionName'
              )}
            />
          </section>
          <div className="h-5" />
        </>
      )}
    </div>
  );
}

/**
 * Component definition of proptypes.
 */
EducationalMaterialCategoriesView.propTypes = {
  educationalCategoryActions: PropTypes.shape({
    getEducationalCategoriesArray: PropTypes.func.isRequired,
    createEducationalCategory: PropTypes.func.isRequired,
    updateEducationalCategory: PropTypes.func.isRequired,
    deleteEducationalCategoryById: PropTypes.func.isRequired
  }).isRequired,
  userActions: PropTypes.shape({
    getValidatePermission: PropTypes.func.isRequired
  })
};
export default EducationalMaterialCategoriesView;
